import CloseIcon from '@mui/icons-material/Close';
import { Button, CircularProgress, Divider, FormControl, FormControlLabel, IconButton, Modal, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import { Users } from '@phosphor-icons/react';
import { useFormik } from 'formik';
import { useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import * as Yup from 'yup';

import SelectDateComponent from './scheduleMetting/selectDateComponent';
import SelectTimeComponent from './scheduleMetting/selectTimeComponent';

import CalendarGrayIcon from '../../../../../assets/svg/calendarGray.svg?react';
import CalendarIcon from '../../../../../assets/svg/calendarPlus.svg?react';
import ClockGrayIcon from '../../../../../assets/svg/clockGray.svg?react';
import PhoneIcon from '../../../../../assets/svg/phoneCall.svg?react';
import VideoIcon from '../../../../../assets/svg/videoCall.svg?react';
import { CALL_VALUES } from '../../../../../constants/meetings';
import { useToastNotificationContext } from '../../../../../hooks/global/alert/useToastNotification';

import { axiosInstance } from '~/assets/utils/axiosInstance';
import { Input } from '~/components/ui/input';
import { CustomError } from '~/interfaces/ErrorType';
import { ScheduleService } from '~/services/api/schedules/endpoints';




interface InstantMeetingModalProps {
  open: boolean;
  setClose: () => void;
  reportId: string;
}

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 480,
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: '12px',
  p: 4,
};

const headerStyle = {
  fontFamily: 'Schibsted Grotesk',
  fontWeight: 500,
  fontSize: '18px',
};

const bodyTextStyle = {
  fontFamily: 'Schibsted Grotesk',
  fontWeight: 400,
  fontSize: '16px',
};

const buttonStyle = {
  textTransform: 'none',
  bgcolor: '#2449FC',
  borderRadius: '50px',
  color: '#FFFFFF',
  fontFamily: 'Schibsted Grotesk',
  fontWeight: 500,
  fontSize: '16px',
  marginTop: '28px',
  '&:hover': {
    bgcolor: '#4E7AFF',
    color: '#FFFFFF',
  },
};
export const combineDateTime = (
  dateStr: string, 
  timeStr: string, 
): Date | null => {
  
  // Parse the date and time strings
  const date = new Date(dateStr);
  const time = new Date(timeStr);
  const hours = time.getHours();
  const minutes = time.getMinutes();
  const seconds = time.getSeconds();
  date.setHours(hours, minutes, seconds);
  return date;
};


const ScheduleMeetingModal = ({ open, setClose, reportId }: InstantMeetingModalProps) => {
  const { createToastNotification } = useToastNotificationContext();
  const intl = useIntl();
  const [isLoading, setIsLoading] = useState<boolean>(false);


  const query = ScheduleService.endpoints.reportSchedules.useQuery(
    {
      reportId: reportId ?? '',
    },
    { skip: !reportId },
  );

  const formik = useFormik({
    initialValues: {
      date: '',
      time: '2024-02-27T00:00:00+08:00',
      callType: null,
      guest_emails: [] as string[],
      email: '',
      submit: null,
    },
    validationSchema: Yup.object({
      date: Yup.string().required(intl.formatMessage({ defaultMessage: 'Date is Required' })),
      time: Yup.string().required(intl.formatMessage({ defaultMessage: 'Time is Required' })),
      callType: Yup.string().required(intl.formatMessage({ defaultMessage: 'Call Type is Required' })),
    }),
    onSubmit: async (values, helpers) => {
      setIsLoading(true);
      try {
        const response = await axiosInstance().post('schedule/meetings', {
          date_and_time: combineDateTime(values.date, values.time),
          type: values.callType,
          report: reportId,
          guest_attendees: values.guest_emails,
        });
        if (response) {
          createToastNotification(intl.formatMessage({
            defaultMessage: 'Meeting successfully scheduled',
          }), 'success');
          formik.resetForm();
          query.refetch();
          setClose();
        }

      } catch (err) {
        helpers.setStatus({ success: false });

        const customError = err as CustomError;
        const errorMessage =
          customError.message ||
          intl.formatMessage({
            defaultMessage: 'Something went wrong, please try again.',
          });
        helpers.setStatus({ success: false });
        helpers.setErrors({ submit: errorMessage });
        helpers.setSubmitting(false);
      }
      setIsLoading(false);
    },
  });

  const handleInviteClick = () => {
    const MAX_EMAILS_LIMIT = 10;
    const { email, guest_emails: emailList } = formik.values;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailList.length === MAX_EMAILS_LIMIT) {
      formik.setFieldError(
        'email',
        intl.formatMessage({
          defaultMessage: 'You can only invite up to 10 guests',
        }),
      );
      return;
    }
    if (!email) {
      formik.setFieldError(
        'email',
        intl.formatMessage({
          defaultMessage: 'Email is required',
        }),
      );
      return;
    }
    if (!emailRegex.test(email)) {
      formik.setFieldError(
        'email',
        intl.formatMessage({
          defaultMessage: 'Invalid email address',
        }),
      );
      return;
    }
    if (emailList.includes(email)) {
      formik.setFieldError(
        'email',
        intl.formatMessage({
          defaultMessage: 'This email is already invited',
        }),
      );
      return;
    }
  
    const updatedEmails = [...emailList, email];
    formik.setFieldValue('guest_emails', updatedEmails);
    formik.setFieldValue('email', ''); 
  };

  return (
    <Modal
      open={open}
      onClose={setClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <form noValidate onSubmit={formik.handleSubmit}>
        <Stack sx={style} gap={3}>
          <Stack direction='row' justifyContent='space-between'>
            <Stack direction='row' gap={1} justifyContent='center' flexGrow={1}>
              <CalendarIcon />
              <Typography sx={headerStyle}>
                <FormattedMessage defaultMessage='Schedule a meet' />
              </Typography>
            </Stack>
            <IconButton sx={{ padding: '0px' }} onClick={setClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <Divider sx={{ width: 'calc(100% + 80px)', marginLeft: '-40px' }} />
          {/* call type */}
          <Stack alignSelf='center'>
            <FormControl id="call-radio-group-label">
              <RadioGroup
                row
                aria-labelledby="call-radio-group-label"
                defaultValue="video"
                name="call-radio-buttons-group"
                sx={{
                  justifyContent: 'center',
                }}
              >
                <FormControlLabel 
                  value="video" 
                  control={<Radio 
                    checked={formik.values.callType === CALL_VALUES.VIDEO}
                    onChange={ () => formik.setFieldValue('callType', CALL_VALUES.VIDEO)
                    }/>} 
                  label={
                    <Stack direction='row' justifyContent='center' gap={1}>
                      <VideoIcon/>
                      <Typography sx={bodyTextStyle}>
                        <FormattedMessage defaultMessage='Video Call' />
                      </Typography>
                    </Stack>
                  } />
                <FormControlLabel 
                  value="phone" 
                  control={<Radio 
                    checked={formik.values.callType === CALL_VALUES.VOICE}
                    onChange={ () => formik.setFieldValue('callType', CALL_VALUES.VOICE)
                    }/>} 
                  label={
                    <Stack direction='row' justifyContent='center' gap={1}>
                      <PhoneIcon/>
                      <Typography sx={bodyTextStyle}>
                        <FormattedMessage defaultMessage='Phone Call'/>
                      </Typography>
                    </Stack>
                  } />
              </RadioGroup>
            </FormControl>
            {formik.touched.callType && formik.errors.callType && 
              <Typography variant='caption' color='error'>
                * {formik.errors.callType}
              </Typography>
            }
          </Stack>
          {/* Select Date */}
          <Stack>
            <Stack direction='row' alignItems='center' justifyContent='space-between' gap={2}>
              <Stack direction='row' alignItems='center' gap={1}>
                <CalendarGrayIcon />
                <Typography sx={{ ...bodyTextStyle, color: '#878787', width: '40px' }}>
                  <FormattedMessage defaultMessage='Date' />
                </Typography>
              </Stack>
              <SelectDateComponent
                selected={formik.values.date}
                setSelected={(val)=> formik.setFieldValue('date', val)}
                disabled={false}
              />
            </Stack>
            {formik.touched.date && formik.errors.date && 
              <Typography variant='caption' color='error'>
                * {formik.errors.date}
              </Typography>
            }
          </Stack>
          {/* Select Time */}
          <Stack>
            <Stack direction='row' alignItems='center' justifyContent='space-between' gap={2}>
              <Stack direction='row' alignItems='center' gap={1}>
                <ClockGrayIcon />
                <Typography sx={{ ...bodyTextStyle, color: '#878787', width: '40px' }}>
                  <FormattedMessage defaultMessage='Time' />
                </Typography>
              </Stack>
              <SelectTimeComponent
                selected={formik.values.time}
                setSelected={(val)=> formik.setFieldValue('time', val)}
                disabled={false}
              />
            </Stack>
            {formik.touched.time && formik.errors.time && 
              <Typography variant='caption' color='error'>
                * {formik.errors.time}
              </Typography>
            }
          </Stack>
          <Divider sx={{ width: 'calc(100% + 80px)', marginLeft: '-40px' }} />

          <Typography color='gray'>
            <FormattedMessage defaultMessage='You can invite an off-platform guest on a scheduled meet. Once invited, a link will be sent via email' />
          </Typography>
          <div className='flex flex-row items-center gap-2'>
            <Users size={32} />
            <Typography color='gray'>
              <FormattedMessage defaultMessage='Guests' />
            </Typography>
            <div>
              <Input 
                fullWidth
                placeholder={intl.formatMessage({ defaultMessage: 'Invite guest by email' })}
                name="emails"
                value={formik.values.email}
                onChange={(e) => formik.setFieldValue('email', e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleInviteClick();
                  }
                }}
              />
              {formik.errors.email && 
                <Typography variant='caption' color='error'>
                  * {formik.errors.email}
                </Typography>
              }
            </div>
            <Button
              onClick={handleInviteClick}
            >
              <FormattedMessage defaultMessage='Invite' />
            </Button>
          </div>
          <div className='overflow-x-auto max-h-48'>
            {formik.values.guest_emails.map((guest, index) => (
              <div key={index} className='flex flex-row justify-between'>
                <Typography >
                  {guest}
                </Typography>
                <IconButton onClick={() => formik.setFieldValue('guest_emails', formik.values.guest_emails.filter((_, i) => i !== index))}>
                  <CloseIcon />
                </IconButton>
              </div>
            ))}
          </div>
          <Button sx={buttonStyle} type='submit' disabled={isLoading}>
            {isLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <FormattedMessage defaultMessage='Send' />}
          </Button>
          {formik.errors.submit && 
            <Typography
              color="error"
              sx={{ mt: 3 }}
              variant="body2"
            >
              {formik.errors.submit}
            </Typography>
          }
        </Stack>
      </form>
    </Modal>
  );
};

export default ScheduleMeetingModal;